import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Input, Modal, FeaturedIcon } from '@unified-checkout/design-system';
import { Store } from '@unified-checkout/design-system/icons/index';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import {
  PAYME_HANDLE_MAX_LENGTH,
  isValidPayMeHandle,
} from '@app/dashboard/views/pages/inner_layout/PayMe/utils/handleValidation';
import styles from './branch-name-modal.module.scss';

export interface Branch {
  id: number;
  name: string;
  isActive: boolean;
}

export interface BranchNameModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (branchName: string) => void;
  onUpdate?: (branchId: number, branchName: string) => void;
  branch?: Branch | null; // Branch to edit, null for adding new
  isUpdateBranchLoading?: boolean; // Loading state for add/update operations
  apiError?: string | null;
  onApiErrorClear?: () => void;
}

export const BranchNameModal = ({
  open,
  onClose,
  onAdd,
  onUpdate,
  branch,
  isUpdateBranchLoading = false,
  apiError = null,
  onApiErrorClear,
}: BranchNameModalProps) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const isEditMode = !!branch;
  const [branchName, setBranchName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize branch name when modal opens or branch changes
  useEffect(() => {
    if (open) {
      if (branch) {
        setBranchName(branch.name);
      } else {
        setBranchName('');
      }
      setValidationError(null);
    }
  }, [open, branch]);

  const validateBranchName = (value: string): string | null => {
    const trimmed = value.trim();

    if (!trimmed) {
      return t(
        'innerLayout.paymePage.branchLinks.addModal.validation.required'
      );
    }

    if (trimmed.length > PAYME_HANDLE_MAX_LENGTH) {
      return t(
        'innerLayout.paymePage.branchLinks.addModal.validation.maxLength'
      );
    }

    if (!isValidPayMeHandle(trimmed)) {
      return t(
        'innerLayout.paymePage.branchLinks.addModal.validation.invalidNomenclature'
      );
    }

    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Limit to max length
    if (value.length <= PAYME_HANDLE_MAX_LENGTH) {
      setBranchName(value);
    }

    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null);
    }

    // Clear API error when user starts typing
    if (apiError && onApiErrorClear) {
      onApiErrorClear();
    }
  };

  // Determine the error to display (API error takes precedence over validation error)
  const displayError = apiError || validationError || undefined;
  const hasError = !!displayError;

  const handleSubmit = () => {
    const validationError = validateBranchName(branchName);

    if (validationError) {
      setValidationError(validationError);
      return;
    }

    const trimmedName = branchName.trim();

    if (isEditMode && branch && onUpdate) {
      // Update existing branch
      onUpdate(branch.id, trimmedName);
    } else {
      // Add new branch
      onAdd(trimmedName);
    }

    // Reset form (notification and modal close will be handled by parent)
    // setBranchName('');
    setValidationError(null);
  };

  const handleClose = () => {
    setBranchName('');
    setValidationError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCloseHandler={handleClose}
      hasCloseIcon={true}
      width="32rem"
      state="default"
      headerIcon={<FeaturedIcon icon={Store} size="xl" color="gray" />}
      keepHeaderIconDefaultColors={true}
      isOverlayAboveModal={true}
      closeOnClickOutside={false}
      title={
        isEditMode
          ? t('innerLayout.paymePage.branchLinks.editModal.title')
          : t('innerLayout.paymePage.branchLinks.addModal.title')
      }
      btnsProps={[
        {
          text: isEditMode
            ? isMobile
              ? t('innerLayout.paymePage.branchLinks.editModal.updateButtonMobile')
              : t('innerLayout.paymePage.branchLinks.editModal.updateButton')
            : isMobile
              ? t('innerLayout.paymePage.branchLinks.addModal.createButtonMobile')
              : t('innerLayout.paymePage.branchLinks.addModal.createButton'),
          variant: 'contained',
          color: 'primary',
          onClickHandler: handleSubmit,
          disabled: !branchName.trim() || (isEditMode && branchName.trim() === branch?.name),
          loading: isUpdateBranchLoading,
        },
        {
          text: t('innerLayout.paymePage.branchLinks.addModal.cancelButton'),
          variant: 'outlined',
          color: 'secondary',
          onClickHandler: handleClose,
          disabled: isUpdateBranchLoading,
        },
      ]}
    >
      <div className={styles['add-branch-modal']}>
        <div className={styles['add-branch-modal__input-section']}>
          <Input
            id="branch-name-input"
            name="branch-name-input"
            type="text"
            value={branchName}
            onChange={handleInputChange}
            placeholder={t(
              'innerLayout.paymePage.branchLinks.addModal.placeholder'
            )}
            label={t('innerLayout.paymePage.branchLinks.branchNameLabel')}
            hintText={displayError ? undefined : t('innerLayout.paymePage.branchLinks.addModal.hintText')}
            error={displayError}
            status={hasError ? 'error' : 'default'}
            autoFocus={!isUpdateBranchLoading}
            disabled={isUpdateBranchLoading}
          />
        </div>
      </div>
    </Modal>
  );
};
